import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  acknowledgeNotification,
  archiveNotification,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
  markTaskNotificationsRead,
} from '@/lib/api/generated/api';

import { notificationKeys } from '../queryKeys';
import { NotificationRequestError, reportNotificationProblem } from '../report';

type MarkAction =
  | { kind: 'one'; notificationId: string }
  | { kind: 'task'; taskId: string }
  | { kind: 'all' }
  | { kind: 'archive'; notificationId: string }
  | { kind: 'unread'; notificationId: string }
  | { kind: 'acknowledge'; notificationId: string };

async function send(action: MarkAction): Promise<number | undefined> {
  switch (action.kind) {
    case 'one': {
      const response = await markNotificationRead(action.notificationId);
      if (response.status !== 200) {
        throw new NotificationRequestError(action.kind, response.status);
      }
      return undefined;
    }
    case 'task': {
      const response = await markTaskNotificationsRead(action.taskId);
      if (response.status !== 200) {
        throw new NotificationRequestError(action.kind, response.status);
      }
      return response.data.unread;
    }
    case 'all': {
      const response = await markAllNotificationsRead();
      if (response.status !== 200) {
        throw new NotificationRequestError(action.kind, response.status);
      }
      return response.data.unread;
    }
    case 'archive': {
      const response = await archiveNotification(action.notificationId);
      if (response.status !== 200) {
        throw new NotificationRequestError(action.kind, response.status);
      }
      return undefined;
    }
    case 'unread': {
      const response = await markNotificationUnread(action.notificationId);
      if (response.status !== 200) {
        throw new NotificationRequestError(action.kind, response.status);
      }
      return undefined;
    }
    case 'acknowledge': {
      // "I'm on it" also reads it (service spec 123); the lists and count
      // are re-read once it settles.
      const response = await acknowledgeNotification(action.notificationId);
      if (response.status !== 200) {
        // 422 is NOT_ACKNOWLEDGEABLE (resolved meanwhile, or never asked for
        // action), 404 gone: the report carries which.
        throw new NotificationRequestError(action.kind, response.status);
      }
      return undefined;
    }
  }
}

/**
 * What the badge reads before the service answers, or `undefined` to wait for
 * its answer instead: only where the outcome is certain from here.
 */
function predictedCount(
  action: MarkAction,
  previous: number | undefined,
): number | undefined {
  switch (action.kind) {
    case 'all':
      return 0;
    case 'unread':
      return (previous ?? 0) + 1;
    // Acknowledging also reads it, but like the others only the service knows
    // whether it was unread a moment ago: wait for its answer.
    case 'one':
    case 'task':
    case 'archive':
    case 'acknowledge':
      return undefined;
  }
}

/**
 * Reading and clearing (spec 131 FR-006). "Mark all read" empties the badge at
 * once, and "Mark unread" (spec 135 US5) adds one to it at once; either is put
 * back if the service refuses. The others settle from the service's answer,
 * which carries the new count where it has one.
 */
export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: send,
    onMutate: (action) => {
      const previous = queryClient.getQueryData<number>(
        notificationKeys.count(),
      );
      const predicted = predictedCount(action, previous);
      if (predicted === undefined) return { previous: undefined };
      queryClient.setQueryData(notificationKeys.count(), predicted);
      return { previous };
    },
    onSuccess: (unread) => {
      if (unread !== undefined) {
        queryClient.setQueryData(notificationKeys.count(), unread);
      }
    },
    onError: (error, action, context) => {
      // The toast tells the person; this tells whoever has to find out why
      // their badge and the service disagreed.
      reportNotificationProblem('mark failed', {
        action: action.kind,
        notificationId:
          'notificationId' in action ? action.notificationId : undefined,
        taskId: 'taskId' in action ? action.taskId : undefined,
        status:
          error instanceof NotificationRequestError ? error.status : undefined,
        error: String(error),
      });
      if (context?.previous !== undefined) {
        queryClient.setQueryData(notificationKeys.count(), context.previous);
      }
      toast.error(
        action.kind === 'archive'
          ? 'That notification could not be archived.'
          : action.kind === 'unread'
            ? 'That notification could not be marked unread.'
            : action.kind === 'acknowledge'
              ? 'That could not be marked as yours.'
              : 'Notifications could not be marked read.',
      );
    },
    onSettled: (_unread, _error, action) => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });
      // The notifications page (spec 147) shows the same rows.
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.pages(),
      });
      if (action.kind !== 'task' && action.kind !== 'all') {
        void queryClient.invalidateQueries({
          queryKey: notificationKeys.count(),
        });
      }
    },
  });
}
